// GrihaniHome.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCarrot,
  faAppleAlt,
  faCheese,
  faBreadSlice,
  faDrumstickBite,
  faUtensils,
  faCalendarAlt,
  faClone,
  faRobot,
  faPlusCircle,
  faListUl,
  faSave,
  faLock,
  faTrashAlt,
  faChartLine,
  faBalanceScale,
  faShirt,
  faSink,
  faPlugCircleBolt 
} from "@fortawesome/free-solid-svg-icons";

import { Line } from "react-chartjs-2";
import "chart.js/auto";

/**
 * GrihaniHome.jsx
 * Mobile-first, dark-theme UI for homemaker module (full spec implemented)
 *
 * Notes:
 * - Uses Tailwind classes for layout & dark theme.
 * - Replace any dummy logic with API calls when integrating backend.
 * - All graphs use react-chartjs-2 Line charts per your request.
 */

const TABS = [
  { id: "Veg", icon: faCarrot },
  { id: "Fruit", icon: faAppleAlt },
  { id: "Dairy", icon: faCheese },
  { id: "Bakery", icon: faBreadSlice },
  { id: "Non-Veg", icon: faDrumstickBite },
  { id: "Outside Food", icon: faUtensils },
  { id: "Cloth Wash", icon: faShirt },
  { id: "Utensil Wash", icon: faSink },
  { id: "Cloth Ironing", icon: faPlugCircleBolt  },
];

const DROPDOWNS = ["Buying Plan", "Spend", "Waste"];
const UNIT_OPTIONS = ["kg", "g", "pcs", "ltr", "bunch", "packet"];

export default function GrihaniHome() {
  // Period controls
  const [periodType, setPeriodType] = useState("week"); // 'date' or 'week'
  const [selectedDate, setSelectedDate] = useState(formatISODate(new Date()));
  const [selectedWeekStart, setSelectedWeekStart] = useState(getStartOfThisWeekISO());

  // UI state
  const [activeTab, setActiveTab] = useState("Veg");
  const [mode, setMode] = useState("Buying Plan"); // Buying Plan, Spend, Waste

  // plans keyed by 'week::YYYY-MM-DD' or 'date::YYYY-MM-DD'
  const [plans, setPlans] = useState(() => getInitialDummyPlans());
  const [currentItems, setCurrentItems] = useState(() => [emptyItem()]);
  const [isFrozen, setIsFrozen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);

  // spend & waste dummy datasets
  const [spendData, setSpendData] = useState(() => getDummySpendRecords());
  const [wasteRecords, setWasteRecords] = useState(() => getDummyWasteRecords());

  // AI simulated
  const [aiResultText, setAiResultText] = useState("");
  const [language, setLanguage] = useState("English");

  // Compare module
  const [compareA, setCompareA] = useState({ type: "week", value: getStartOfThisWeekISO() });
  const [compareB, setCompareB] = useState({ type: "week", value: addWeeksISO(getStartOfThisWeekISO(), -1) });
  const [compareMetric, setCompareMetric] = useState("Plan"); // Plan, Actual, Waste

  // derived
  const periodKey = (type = periodType, value = periodType === "date" ? selectedDate : selectedWeekStart) =>
    `${type}::${value}`;

  // EFFECT: if user switches tab, optionally load plan items for that tab if present
  useEffect(() => {
    const key = periodKey();
    const plan = plans[key];
    if (plan && plan.tab === activeTab) {
      setCurrentItems(plan.items.map((it) => ({ ...it })));
      setIsFrozen(!!plan.frozen);
    } else {
      // default single empty item for new tab
      setCurrentItems([emptyItem()]);
      setIsFrozen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, periodType, selectedDate, selectedWeekStart]);

  // ---------- Core actions ----------

  function handleAddItem() {
    setCurrentItems((s) => [...s, emptyItem()]);
  }

  function handleUpdateItem(idx, field, value) {
    setCurrentItems((s) => s.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  }

  function handleRemoveItem(idx) {
    setCurrentItems((s) => s.filter((_, i) => i !== idx));
  }

  function handleSavePlan() {
    const key = periodKey();
    const newPlan = {
      periodType,
      periodValue: periodType === "date" ? selectedDate : selectedWeekStart,
      tab: activeTab,
      items: currentItems.map((it) => ({ ...it })),
      frozen: isFrozen,
      updatedAt: new Date().toISOString(),
    };
    setPlans((p) => ({ ...p, [key]: newPlan }));
    // Optionally aggregate spend/waste locally (demo)
    aggregateDummyFromPlan(newPlan, setSpendData, setWasteRecords);
    alert("Plan saved locally (dummy). Integrate your backend in the save handler.");
  }

  function handleClone(from) {
    // from: { type: 'date'|'week', value: 'YYYY-MM-DD' }
    setCloneSource(from);
    const key = `${from.type}::${from.value}`;
    const plan = plans[key];
    if (plan) {
      setCurrentItems(plan.items.map((it) => ({ ...it, id: newId() })));
      setIsFrozen(false);
      alert(`Cloned ${plan.items.length} items from ${from.type} ${from.value}`);
    } else {
      alert("No plan found to clone for selected period.");
    }
  }

  function handlePlanWithAI() {
    const prompt = generatePrompt(activeTab, periodType === "date" ? selectedDate : selectedWeekStart, currentItems, language);
    const ai = fakeAiSuggest(prompt);
    setAiResultText(ai.prompt + "\n\n" + ai.suggestionText);
    // insert AI items into form (editable)
    setCurrentItems(ai.items.map((it) => ({ ...it, id: newId() })));
    setIsFrozen(false);
  }

  function toggleFreeze() {
    setIsFrozen((f) => !f);
  }

  // Compare results computation
  function computeCompare(a, b, metric) {
    // a,b: {type, value}
    const aKey = `${a.type}::${a.value}`;
    const bKey = `${b.type}::${b.value}`;
    const getMetric = (k) => {
      const plan = plans[k];
      if (metric === "Plan") {
        return plan ? plan.items.reduce((s, it) => s + Number(it.targetBudget || 0), 0) : 0;
      } else if (metric === "Actual") {
        return plan ? plan.items.reduce((s, it) => s + Number(it.actualSpend || 0), 0) : 0;
      } else if (metric === "Waste") {
        // sum wasteRecords entries matching periodKey
        return wasteRecords.filter((w) => w.periodKey === k).reduce((s, r) => s + Number(r.amount || 0), 0);
      }
      return 0;
    };
    return { a: getMetric(aKey), b: getMetric(bKey) };
  }

  // ---------- Derived data for charts ----------

  const trendChartData = useMemo(() => {
    // produce last 8 weeks trend combining spendData (week records exist in dummy)
    const weeks = [...Array(8)].map((_, i) => addWeeksISO(getStartOfThisWeekISO(), -i)).reverse();
    return {
      labels: weeks.map((w) => w.slice(5)), // mm-dd for compact
      datasets: [
        {
          label: "Budgeted",
          data: weeks.map((w) => findSpendRecord(spendData, `week::${w}`).budgeted),
          tension: 0.3,
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59,130,246,0.2)",
        },
        {
          label: "Actual",
          data: weeks.map((w) => findSpendRecord(spendData, `week::${w}`).actual),
          tension: 0.3,
          borderColor: "#f59e0b",
          backgroundColor: "rgba(245,158,11,0.16)",
        },
      ],
    };
  }, [spendData]);

  const wasteChartDataForCurrent = useMemo(() => {
    const key = periodKey();
    // sum wasteRecords for that key
    const recs = wasteRecords.filter((r) => r.periodKey === key);
    const byItem = {};
    recs.forEach((r) => {
      byItem[r.item] = (byItem[r.item] || 0) + Number(r.amount || 0);
    });
    const labels = Object.keys(byItem);
    const data = labels.map((k) => byItem[k]);
    return {
      labels: labels.length ? labels : ["No data"],
      datasets: [
        {
          label: "Waste ₹",
          data: data.length ? data : [0],
          tension: 0.3,
          borderColor: "#ef4444",
          backgroundColor: "rgba(239,68,68,0.18)",
        },
      ],
    };
  }, [wasteRecords, periodType, selectedDate, selectedWeekStart, plans]);

  // ---------- helpers & dummy implementations ----------

  function viewCurrentList() {
    // show simple list - can be replaced by modal
    alert(JSON.stringify(currentItems, null, 2));
  }

  // ---------- UI rendering ----------

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 [&_p]:text-white [&_li]:text-gray-100 p-4 sm:p-6 font-sans">
      <div className="mx-auto">
        <header className="flex flex-col items-start justify-left mb-4 gap-2">
            {/* Left side — Title */}
            <div className="flex flex-col">
                <h1 className="flex text-start text-xl lg:text-2xl font-bold text-amber-400">🏡 Grihani</h1>
                <div className="text-sm text-gray-400">
                Home — Planner · Spend · Waste · Compare
                </div>
            </div>

            {/* Right side — Quick Links */}
            <div className="flex flex-row gap-4 text-xs text-gray-400 text-right">
                <div className="mt-1 flex flex-row justify-center gap-1">
                <a
                    href="#ai-assisted"
                    className="text-amber-300 hover:text-amber-200 transition"
                >
                    <i className="fas fa-robot text-amber-300"></i>
                    <span>  AI-assisted</span>
                </a>
                </div>
                <div className="mt-1">
                <a
                    href="#about-grihani"
                    className="text-amber-300 hover:text-amber-200 transition"
                >
                     <i className="fas fa-question text-amber-300"></i>
                    <span>  About Grihani</span>
                </a>
                </div>
            </div>
            </header>


        {/* Period selector */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-4 shadow">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-sm">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="bg-gray-700 text-gray-200 p-1 rounded h-10"
              >
                <option value="week">Week</option>
                <option value="date">Date</option>
              </select>
            </div>

            {periodType === "date" ? (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="ml-auto bg-gray-700 text-gray-200 p-2 rounded text-sm"
              />
            ) : (
              <div className="ml-auto flex items-center gap-2">
                <button
                  className="px-2 py-1 w-10 rounded bg-gray-700 text-sm"
                  onClick={() => setSelectedWeekStart(addWeeksISO(selectedWeekStart, -1))}
                >
                  Prev
                </button>
                <div className="text-sm">Week of {selectedWeekStart}</div>
                <button
                  className="px-2 py-1 w-10 rounded bg-gray-700 text-sm"
                  onClick={() => setSelectedWeekStart(addWeeksISO(selectedWeekStart, 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Tabs and mode */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-4 shadow">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 place-items-center">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-2xl text-sm whitespace-nowrap ${
                  activeTab === t.id ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"
                }`}
              >
                <FontAwesomeIcon icon={t.icon} /> <span>{t.id}</span>
              </button>
            ))}
          </div>

          <div className="mt-3 flex gap-2 justify-center items-center">
            <select className="h-10 w-40 p-2 rounded bg-gray-700 text-gray-200" value={mode} onChange={(e) => setMode(e.target.value)}>
              {DROPDOWNS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <button className="px-3 py-2 mt-4 w-20 rounded bg-green-600 text-sm" onClick={handleSavePlan}>
              <FontAwesomeIcon icon={faSave} /> Save
            </button>
          </div>
        </section>

        {/* Form / Plan area */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-4 shadow">
          <div className="flex flex-col items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faClone} />
              <div className="text-sm">Plan (Tab: {activeTab})</div>
            </div>

            <div className="flex items-center gap-2 text-sm">
              {/* clone choices */}
              <select
                onChange={(e) => {
                  // quick clone example: option value like 'week::2025-09-29' or 'date::2025-10-05'
                  const v = e.target.value;
                  if (!v) return;
                  const [type, value] = v.split("::");
                  handleClone({ type, value });
                }}
                className="bg-gray-700 text-gray-200 p-1 h-10 rounded mr-2"
              >
                <option value="">-- Clone from --</option>
                {Object.keys(plans).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>

              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="bg-gray-700 text-gray-200 h-10 p-1 rounded">
                <option>English</option>
                <option>Hindi</option>
                <option>Marathi</option>
                <option>Gujarati</option>
                <option>Bengali</option>
              </select>
            </div>
            <div className="flex justify-center mt-2">
              <button className="px-2 py-1 w-40 rounded bg-blue-600 text-sm" onClick={handlePlanWithAI}>
                  <FontAwesomeIcon icon={faRobot} /> Plan with AI
                </button>
            </div>
          </div>

          {/* AI prompt/result preview (editable) */}
          <div className="mb-3">
            <label className="text-xs text-gray-300">AI Prompt / Result</label>
            <textarea
              readOnly
              value={generatePrompt(activeTab, periodType === "date" ? selectedDate : selectedWeekStart, currentItems, language) + (aiResultText ? "\n\nAI Result:\n" + aiResultText : "")}
              className="w-full mt-1 p-2 rounded bg-gray-700 text-gray-200 text-sm h-24"
            />
          </div>

          {/* items list */}
          <div>
            {currentItems.map((it, idx) => (
              <div key={it.id} className="mb-3 p-2 bg-gray-900/40 rounded">
                <div className="flex gap-2">
                  <input
                    value={it.item}
                    onChange={(e) => handleUpdateItem(idx, "item", e.target.value)}
                    placeholder="Item"
                    className="flex-1 p-2 rounded bg-gray-800 text-gray-200 text-sm"
                  />
                  <input
                    value={it.qty}
                    onChange={(e) => handleUpdateItem(idx, "qty", e.target.value)}
                    placeholder="Qty"
                    className="w-20 p-2 rounded bg-gray-800 text-gray-200 text-sm"
                  />
                  <select value={it.unit} onChange={(e) => handleUpdateItem(idx, "unit", e.target.value)} className="w-24 h-10 p-2 rounded bg-gray-800 text-gray-200 text-sm">
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                  <input
                    value={it.targetBudget}
                    onChange={(e) => handleUpdateItem(idx, "targetBudget", e.target.value)}
                    placeholder="Target Budget (₹)"
                    className="p-2 rounded bg-gray-800 text-gray-200"
                  />
                  <input
                    value={it.actualSpend}
                    onChange={(e) => handleUpdateItem(idx, "actualSpend", e.target.value)}
                    placeholder="Actual Spend (₹)"
                    className="p-2 rounded bg-gray-800 text-gray-200"
                  />
                  <input
                    value={it.approxWasteQty}
                    onChange={(e) => handleUpdateItem(idx, "approxWasteQty", e.target.value)}
                    placeholder="Approx Waste Qty"
                    className="p-2 rounded bg-gray-800 text-gray-200"
                  />
                  <input
                    value={it.approxWasteAmount}
                    onChange={(e) => handleUpdateItem(idx, "approxWasteAmount", e.target.value)}
                    placeholder="Approx Waste Amount (₹)"
                    className="p-2 rounded bg-gray-800 text-gray-200"
                  />
                </div>

                <div className="mt-2 flex flex-col gap-2 items-center text-sm">
                  <select value={it.person} onChange={(e) => handleUpdateItem(idx, "person", e.target.value)} className="p-2 rounded h-10 bg-gray-800 text-gray-200">
                    <option>Self</option>
                    <option>Family</option>
                    <option>Guest</option>
                  </select>
                  <input value={it.buyer} onChange={(e) => handleUpdateItem(idx, "buyer", e.target.value)} placeholder="Buyer Name" className="p-2 rounded bg-gray-800 text-gray-200 flex-1 text-sm" />
                  <input value={it.market} onChange={(e) => handleUpdateItem(idx, "market", e.target.value)} placeholder="Market / Shop" className="p-2 rounded bg-gray-800 text-gray-200 w-36 text-sm" />
                </div>

                <div className="mt-2 flex gap-2 items-center">
                  <input value={it.remarks} onChange={(e) => handleUpdateItem(idx, "remarks", e.target.value)} placeholder="Remarks" className="p-2 rounded bg-gray-800 text-gray-200 flex-1 text-sm" />
                  <button onClick={() => handleRemoveItem(idx)} className="p-2 mt-4 rounded w-10 bg-red-700 text-white text-sm"><FontAwesomeIcon icon={faTrashAlt} /></button>
                </div>
              </div>
            ))}

            <div className="grid grid-cols-3 gap-2 place-items-center">
              <button onClick={handleAddItem} className="flex-1 px-3 py-2 rounded bg-indigo-600 w-32 text-white text-sm"><FontAwesomeIcon icon={faPlusCircle} /> Add More</button>
              <button onClick={viewCurrentList} className="px-3 py-2 rounded bg-cyan-700 w-40 text-sm"><FontAwesomeIcon icon={faListUl} /> View List</button>
              <label className="flex items-center gap-2 px-3 py-2 rounded bg-teal-600 text-sm">
                <input type="checkbox" checked={isFrozen} onChange={toggleFreeze} className="ml-1 w-32" /> <FontAwesomeIcon icon={faLock} /> Freeze Plan
              </label>
            </div>
          </div>
        </section>

        {/* Spend Analyzer */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-4 shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><FontAwesomeIcon icon={faChartLine} /> <div className="font-medium">Spend Analyzer</div></div>
            <div className="text-xs text-gray-400">{periodType === "date" ? selectedDate : selectedWeekStart}</div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-sm mb-3">
            {(() => {
              const key = periodKey();
              const plan = plans[key];
              const budgeted = plan ? plan.items.reduce((s, it) => s + Number(it.targetBudget || 0), 0) : findSpendRecord(spendData, key).budgeted;
              const actual = plan ? plan.items.reduce((s, it) => s + Number(it.actualSpend || 0), 0) : findSpendRecord(spendData, key).actual;
              const diff = Math.round(actual - budgeted);
              return (
                <>
                  <div className="p-2 rounded bg-gray-900/40 text-center">Budgeted<br/><span className="font-semibold">₹{budgeted}</span></div>
                  <div className="p-2 rounded bg-gray-900/40 text-center">Actual<br/><span className="font-semibold">₹{actual}</span></div>
                  <div className={`p-2 rounded text-center ${diff>0 ? "bg-red-900/40 text-red-300" : "bg-green-900/40 text-green-300"}`}>Difference<br/><span className="font-semibold">₹{diff}</span></div>
                </>
              );
            })()}
          </div>

          <div className="h-40">
            <Line data={trendChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </section>

        {/* Waste Analyzer */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-4 shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><FontAwesomeIcon icon={faTrashAlt} /> <div className="font-medium">Waste Analyzer</div></div>
            <div className="text-xs text-gray-400">{periodType === "date" ? selectedDate : selectedWeekStart}</div>
          </div>

          <div className="mb-3 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-400 text-xs">
                <tr>
                  <th className="pb-2">Item</th>
                  <th className="pb-2">Waste Qty</th>
                  <th className="pb-2">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const key = periodKey();
                  const recs = wasteRecords.filter((r) => r.periodKey === key);
                  if (!recs.length) {
                    return (
                      <tr>
                        <td colSpan="3" className="py-3 text-gray-400">No waste data for this period.</td>
                      </tr>
                    );
                  }
                  return recs.map((r, i) => (
                    <tr key={i} className="border-t border-gray-800">
                      <td className="py-2">{r.item}</td>
                      <td className="py-2">{r.qty} {r.unit}</td>
                      <td className="py-2">₹{r.amount}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>

          <div className="h-36">
            <Line data={wasteChartDataForCurrent} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </section>

        {/* Compare module */}
        <section className="bg-gray-800 rounded-2xl p-3 mb-8 shadow">
          <div className="flex items-center justify-between mb-2">
            <div className="flex w-20 items-center gap-2"><FontAwesomeIcon icon={faBalanceScale} /> <div className="font-medium">Compare</div></div>
            <div className="text-xs text-gray-400">Select two periods</div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-xs text-gray-400 mb-1">A</div>
                <select className="w-full h-10 bg-gray-700 p-2 rounded text-sm" value={compareA.type} onChange={(e) => setCompareA({ ...compareA, type: e.target.value })}>
                  <option value="week">Week</option>
                  <option value="date">Date</option>
                </select>
                <input
                  type="date"
                  value={compareA.value}
                  onChange={(e) => setCompareA({ ...compareA, value: e.target.value })}
                  className="w-full bg-gray-700 p-2 rounded mt-2 text-sm"
                />
              </div>

              <div>
                <div className="text-xs text-gray-400 mb-1">B</div>
                <select className="w-full h-10 bg-gray-700 p-2 rounded text-sm" value={compareB.type} onChange={(e) => setCompareB({ ...compareB, type: e.target.value })}>
                  <option value="week">Week</option>
                  <option value="date">Date</option>
                </select>
                <input
                  type="date"
                  value={compareB.value}
                  onChange={(e) => setCompareB({ ...compareB, value: e.target.value })}
                  className="w-full bg-gray-700 p-2 rounded mt-2 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <select value={compareMetric} onChange={(e) => setCompareMetric(e.target.value)} className="bg-gray-700 h-10 p-2 rounded text-sm">
                <option>Plan</option>
                <option>Actual</option>
                <option>Waste</option>
              </select>
              <button
                onClick={() => {
                  const res = computeCompare(compareA, compareB, compareMetric);
                  alert(`Compare ${compareMetric}\nA: ₹${res.a}\nB: ₹${res.b}`);
                }}
                className="px-3 py-2 w-20 rounded bg-blue-600 text-sm"
              >
                Compare
              </button>
            </div>

            <div className="h-40">
              {/* For compare, show a two-point line */}
              {(() => {
                const res = computeCompare(compareA, compareB, compareMetric);
                const data = {
                  labels: ["A", "B"],
                  datasets: [
                    {
                      label: `${compareMetric} (₹)`,
                      data: [res.a, res.b],
                      borderColor: "#7c3aed",
                      backgroundColor: "rgba(124,58,237,0.16)",
                      tension: 0.3,
                    },
                  ],
                };
                return <Line data={data} options={{ responsive: true, maintainAspectRatio: false }} />;
              })()}
            </div>
          </div>
        </section>

        {/* 🏡 About Grihani Section */}
        <section id="about-grihani" className="bg-gray-900 text-gray-100 py-10 px-4 md:px-10 rounded-2xl shadow-lg mt-6">
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-amber-400 mb-2">
                <i className="fas fa-house-user mr-2"></i> About Grihani
                </h2>
                <p className="text-sm text-gray-400">AI-powered home expense and daily chore management companion</p>
            </div>

            {/* Executive Summary */}
            <div className="mb-6">
                <p className="leading-relaxed text-gray-300">
                <span className="font-semibold text-amber-200">Grihani</span> is a digital home-management
                assistant designed to empower homemakers with data-driven tools for meal planning, household
                budgeting, and waste reduction. By turning traditional household routines into intelligent,
                AI-assisted workflows, it helps every homemaker make smarter purchase decisions, reduce waste,
                and balance their monthly budgets — all through a simple, mobile-friendly dashboard that
                supports multiple Indian languages.
                </p>
            </div>

            {/* Problem Statement */}
            <div className="mb-6">
                <h3 className="text-xl font-semibold text-amber-300 mb-2">
                <i className="fas fa-triangle-exclamation mr-2"></i> Problems for Grihanis:
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>Unplanned weekly purchases leading to budget overruns.</li>
                <li>Food waste due to poor estimation or lack of tracking.</li>
                <li>Difficulty analyzing spending trends and waste patterns.</li>
                <li>No structured way to compare past weeks or months for improvement.</li>
                <li>Limited access to AI-based support for household optimization.</li>
                </ul>
            </div>

            {/* Proposed Solution */}
            <div className="mb-6">
                <h3 className="text-xl font-semibold text-amber-300 mb-2">
                <i className="fas fa-lightbulb mr-2"></i> Proposed Solution
                </h3>
                <p className="leading-relaxed text-gray-300 mb-2">
                The <span className="font-semibold text-amber-200">Grihani Module</span> acts as a personal
                home economist — integrating AI-assisted planning, expense analytics, and waste tracking into
                one intelligent system. Homemakers can:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>Plan weekly shopping by category (Veg, Fruits, Dairy, etc.).</li>
                <li>Clone or reuse plans from previous weeks for convenience.</li>
                <li>Use AI to auto-generate meal or shopping plans in regional languages.</li>
                <li>Analyze budgets vs. actual spends through simple visual summaries.</li>
                <li>Compare spending and waste patterns across time periods.</li>
                </ul>
            </div>

            {/* Functional Specifications */}
            <div>
                {/* <h3 className="text-xl font-semibold text-amber-300 mb-2">
                <i className="fas fa-cogs mr-2"></i> Functional Specifications
                </h3> */}
                <p className="leading-relaxed text-gray-300 mb-3">
                The <span className="font-semibold text-amber-200">Grihani Dashboard</span> unifies all
                household planning and analytics in one intuitive screen.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-200">
                <div className="bg-gray-800 p-4 rounded-xl shadow">
                    <h4 className="font-semibold text-amber-400 mb-2">
                    <i className="fas fa-table mr-2"></i> Key Functionalities
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-300">
                    <li>Select period by Date or Week.</li>
                    <li>Category Tabs: Veg, Fruit, Dairy, Bakery, Non-Veg, Outside Food.</li>
                    <li>Dropdown Options: Buying Plan, Spend, Waste.</li>
                    <li>Clone plan from a selected date or week.</li>
                    <li>“Plan with AI” button for auto-generated suggestions.</li>
                    </ul>
                </div>

                <div className="bg-gray-800 p-4 rounded-xl shadow">
                    <h4 className="font-semibold text-amber-400 mb-2">
                    <i className="fas fa-clipboard-list mr-2"></i> Data Input Fields
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-300">
                    <li>Item, Quantity, Unit, Target Budget, Actual Spend</li>
                    <li>Approx. Waste Qty & Value, Person, Buyer, Market/Shop</li>
                    <li>Remarks, Add More, View List, and Freeze Plan</li>
                    </ul>
                </div>
                </div>
            </div>
        </section>

        {/* 🤖 AI-Assisted Section */}
        <section id="ai-assisted" className="bg-gray-900 text-gray-100 py-4 px-4 md:px-10 rounded-2xl shadow-lg mt-2">
            <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-amber-400 mb-2">
                <i className="fas fa-robot mr-2"></i> AI-Assisted
                </h2>
                <p className="text-sm text-gray-400">Smart planning made easy with AI guidance</p>
            </div>

            {/* Executive Explanation */}
            <div className="mb-6">
                <p className="leading-relaxed text-gray-300">
                The <span className="font-semibold text-amber-200">Grihani Module</span> helps you generate
                AI prompts to assist in creating a proper household plan. You can use these prompts with AI
                tools like ChatGPT to generate detailed meal, grocery, or shopping plans.
                </p>
            </div>

            {/* How it Works */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold text-amber-300 mb-2">
                <i className="fas fa-lightbulb mr-2"></i> How it Works
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>Type or select your requirements in natural language (with language preference).</li>
                <li>The system generates an AI-friendly prompt for your input.</li>
                <li>Copy this prompt and paste it into an AI tool like ChatGPT.</li>
                <li>Receive the AI-generated plan and paste the result back into Grihani.</li>
                <li>Click <span className="font-semibold text-amber-200">"Auto Fill My Planning Data From AI Result"</span> to populate your form automatically.</li>
                </ul>
            </div>

            {/* Benefits */}
            <div>
                <h3 className="text-lg font-semibold text-amber-300 mb-2">
                <i className="fas fa-bolt mr-2"></i> Benefits
                </h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                <li>Save time by quickly generating detailed shopping and meal plans.</li>
                <li>Reduce errors and improve accuracy in planning and budgeting.</li>
                <li>Leverage AI suggestions while keeping control over final inputs.</li>
                <li>Plan in your preferred language for maximum convenience.</li>
                </ul>
            </div>
        </section>


        <footer className="text-center text-xs text-gray-500 mb-8">Grihani — By Think5Whys Global DeepTech Solutions</footer>
      </div>
    </div>
  );
}

/* ---------------------- Helpers & Dummy data ---------------------- */

function formatISODate(d) {
  return new Date(d).toISOString().slice(0, 10);
}

function getStartOfThisWeekISO() {
  const d = new Date();
  const day = d.getDay(); // 0 Sun .. 6 Sat
  // Make Monday the start: if Sunday => go back 6
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  return monday.toISOString().slice(0, 10);
}

function addWeeksISO(isoDate, n) {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + n * 7);
  return d.toISOString().slice(0, 10);
}

function newId() {
  return Math.random().toString(36).slice(2, 9);
}

function emptyItem() {
  return {
    id: newId(),
    item: "",
    qty: "",
    unit: "kg",
    targetBudget: "",
    actualSpend: "",
    approxWasteQty: "",
    approxWasteAmount: "",
    person: "Self",
    buyer: "",
    market: "",
    remarks: "",
  };
}

/* Dummy initial plans keyed by period */
function getInitialDummyPlans() {
  const wk = getStartOfThisWeekISO();
  const lastwk = addWeeksISO(wk, -1);
  const today = formatISODate(new Date());
  const plans = {};
  plans[`week::${wk}`] = {
    periodType: "week",
    periodValue: wk,
    tab: "Veg",
    items: [
      { ...emptyItem(), item: "Potato", qty: "2", unit: "kg", targetBudget: 80, actualSpend: 70, approxWasteQty: 0.1, approxWasteAmount: 4, person: "Family", buyer: "Me", market: "Local Market", remarks: "" },
      { ...emptyItem(), item: "Onion", qty: "1.5", unit: "kg", targetBudget: 60, actualSpend: 55, approxWasteQty: 0.2, approxWasteAmount: 8, person: "Family", buyer: "Me", market: "Local Market", remarks: "" },
    ],
    frozen: false,
  };
  plans[`week::${lastwk}`] = {
    periodType: "week",
    periodValue: lastwk,
    tab: "Fruit",
    items: [{ ...emptyItem(), item: "Banana", qty: "12", unit: "pcs", targetBudget: 120, actualSpend: 110, approxWasteQty: 1, approxWasteAmount: 10, person: "Family", buyer: "Soni", market: "Fruit Shop", remarks: "" }],
    frozen: true,
  };
  plans[`date::${today}`] = {
    periodType: "date",
    periodValue: today,
    tab: "Dairy",
    items: [{ ...emptyItem(), item: "Milk", qty: "2", unit: "ltr", targetBudget: 100, actualSpend: 90, approxWasteQty: 0, approxWasteAmount: 0, person: "Self", buyer: "Me", market: "Dairy", remarks: "" }],
    frozen: false,
  };
  return plans;
}

/* Dummy spend records */
function getDummySpendRecords() {
  const wk = getStartOfThisWeekISO();
  const arr = [];
  for (let i = 0; i < 12; i++) {
    const w = addWeeksISO(wk, -i);
    arr.push({ periodKey: `week::${w}`, budgeted: Math.round(1500 - i * 20), actual: Math.round(1200 + Math.random() * 600) });
  }
  const today = formatISODate(new Date());
  arr.push({ periodKey: `date::${today}`, budgeted: 300, actual: 280 });
  return arr;
}

/* Dummy waste records */
function getDummyWasteRecords() {
  const wk = getStartOfThisWeekISO();
  return [
    { periodKey: `week::${wk}`, item: "Potato", qty: 0.5, unit: "kg", amount: 10 },
    { periodKey: `week::${wk}`, item: "Onion", qty: 0.2, unit: "kg", amount: 5 },
    { periodKey: `date::${formatISODate(new Date())}`, item: "Milk", qty: 0.5, unit: "ltr", amount: 25 },
    { periodKey: `week::${addWeeksISO(wk, -1)}`, item: "Banana", qty: 1, unit: "pcs", amount: 12 },
  ];
}

function findSpendRecord(spendData, key) {
  const rec = spendData.find((s) => s.periodKey === key);
  if (rec) return rec;
  // fallback random-ish
  return { periodKey: key, budgeted: 400, actual: Math.round(300 + Math.random() * 600) };
}

/* Adds spend/waste from plan into dummy arrays (client-side only) */
function aggregateDummyFromPlan(plan, setSpendData, setWasteRecords) {
  // This is a simple demo aggregator that updates local arrays.
  // Replace with API calls to persist to real DB.
  const key = `${plan.periodType}::${plan.periodValue}`;
  const budgeted = plan.items.reduce((s, it) => s + Number(it.targetBudget || 0), 0);
  const actual = plan.items.reduce((s, it) => s + Number(it.actualSpend || 0), 0);
  // spend
  setSpendData((prev) => {
    const filtered = prev.filter((p) => p.periodKey !== key);
    return [...filtered, { periodKey: key, budgeted, actual }];
  });
  // waste
  const wasteRecs = plan.items
    .filter((it) => Number(it.approxWasteAmount || 0) > 0)
    .map((it) => ({ periodKey: key, item: it.item || "Unknown", qty: it.approxWasteQty || 0, unit: it.unit || "", amount: Number(it.approxWasteAmount || 0) }));
  if (wasteRecs.length) {
    setWasteRecords((prev) => {
      const filtered = prev.filter((p) => p.periodKey !== key);
      return [...filtered, ...wasteRecs];
    });
  }
}

/* AI prompt generator & fake AI responder */
function generatePrompt(tab, period, items, language) {
  const itemList = items.length ? items.map((it) => `${it.item || "ITEM"}(${it.qty || "-"}/${it.unit || "-"})`).join(", ") : "No items";
  return `Generate a shopping & meal plan for ${tab} for ${period} in ${language}. Current items: ${itemList}. Provide budget-friendly shopping list, quantity suggestions and waste-minimizing tips.`;
}

function fakeAiSuggest(prompt) {
  // Very simple fake generation
  const suggestionText = "Suggested: Buy seasonal vegetables, avoid overbuying fruits, plan 2 vegetable-based meals and 1 outside-food day. Reduce waste by preserving leftovers.";
  const items = [
    { ...emptyItem(), item: "Tomato", qty: "1", unit: "kg", targetBudget: 60, actualSpend: "", approxWasteQty: "", approxWasteAmount: "" },
    { ...emptyItem(), item: "Spinach", qty: "1", unit: "bunch", targetBudget: 30, actualSpend: "", approxWasteQty: "", approxWasteAmount: "" },
    { ...emptyItem(), item: "Paneer", qty: "250", unit: "g", targetBudget: 120, actualSpend: "", approxWasteQty: "", approxWasteAmount: "" },
  ];
  return { prompt, suggestionText, items };
}
